/**
 * Menu Modal Component
 * Unified modal for creating and editing menus
 *
 * For MENU type: Multi-recipe selection (required) + custom title
 * For ANNOUNCEMENT type: Only text input for announcement message
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { createMenu, updateMenu } from '@/lib/api/menus';
import { fetchMembers } from '@/lib/api/members';
import { toISODate, getWeekRange, getWeekDates, fromISODate, formatDate } from '@/lib/utils/dateHelpers';
import { MultiRecipeSelector } from '@/components/admin/MultiRecipeSelector';
import type { SelectedRecipe } from '@/components/admin/MultiRecipeSelector';
import { Copy, ExternalLink, Trash2 } from 'lucide-react';
import type { Menu, Member } from '@/types/api';
import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const CUTOFF_TIMEZONE = 'America/Los_Angeles';

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const pacificFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: CUTOFF_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function getPacificParts(date: Date): ZonedParts {
  const parts = pacificFormatter.formatToParts(date);
  const lookup = (type: string) => parts.find((part) => part.type === type)?.value ?? '0';

  return {
    year: Number.parseInt(lookup('year'), 10),
    month: Number.parseInt(lookup('month'), 10),
    day: Number.parseInt(lookup('day'), 10),
    hour: Number.parseInt(lookup('hour'), 10),
    minute: Number.parseInt(lookup('minute'), 10),
    second: Number.parseInt(lookup('second'), 10),
  };
}

function makePacificDate(parts: ZonedParts): Date {
  const utcGuess = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  ));
  const zonedGuess = getPacificParts(utcGuess);
  const utcFromZonedGuess = Date.UTC(
    zonedGuess.year,
    zonedGuess.month - 1,
    zonedGuess.day,
    zonedGuess.hour,
    zonedGuess.minute,
    zonedGuess.second
  );
  return new Date(utcGuess.getTime() - (utcFromZonedGuess - utcGuess.getTime()));
}

function toPacificDateInputValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = getPacificParts(date);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

function cutoffDateToIso(value?: string | null): string | null {
  if (!value || !DATE_REGEX.test(value)) return null;
  const [yearPart, monthPart, dayPart] = value.split('-');
  if (!yearPart || !monthPart || !dayPart) return null;
  const year = Number.parseInt(yearPart, 10);
  const month = Number.parseInt(monthPart, 10);
  const day = Number.parseInt(dayPart, 10);
  const date = makePacificDate({ year, month, day, hour: 23, minute: 59, second: 0 });
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

// Validation schema - title only required for ANNOUNCEMENT
const menuFormSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
  title: z.string().max(255, 'Title too long').optional(),
  type: z.enum(['MENU', 'ANNOUNCEMENT']),
  rsvpAudience: z.enum(['FMB_ONLY', 'JAMAAT_WIDE']),
  rsvpCutoffAt: z.string().optional().nullable(),
});

type MenuFormData = z.infer<typeof menuFormSchema>;

interface MenuModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menu?: Menu | null;
  defaultDate?: Date;
  onSuccess: () => void;
  onDelete?: (menu: Menu) => void;
}

/**
 * MenuModal - Create or edit a menu
 *
 * @param menu - If provided, modal is in edit mode
 * @param defaultDate - Default date for new menus
 */
export function MenuModal({
  open,
  onOpenChange,
  menu,
  defaultDate,
  onSuccess,
  onDelete,
}: MenuModalProps) {
  const { showToast } = useUIStore();
  const isEditMode = !!menu;
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Track recipes, caterer, and cooking day selections
  const [selectedRecipes, setSelectedRecipes] = useState<SelectedRecipe[]>([]);
  const [selectedAssignedCatererId, setSelectedAssignedCatererId] = useState<string | null>(null);
  const [caterers, setCaterers] = useState<Member[]>([]);
  const [selectedCookingDate, setSelectedCookingDate] = useState<string | null>(null);
  // Track whether the user has manually edited the title (stops auto-populate)
  const [userEditedTitle, setUserEditedTitle] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MenuFormData>({
    resolver: zodResolver(menuFormSchema),
    defaultValues: {
      date: defaultDate ? toISODate(defaultDate) : '',
      title: '',
      type: 'MENU',
      rsvpAudience: 'FMB_ONLY',
      rsvpCutoffAt: '',
    },
  });

  const menuType = watch('type');
  const menuDate = watch('date');
  const rsvpAudience = watch('rsvpAudience');
  const isJamaatWide = rsvpAudience === 'JAMAAT_WIDE';

  const publicRsvpLink = useMemo(() => {
    if (!menu?.publicRsvpSlug) return '';
    return `${window.location.origin}/public-rsvp/${menu.publicRsvpSlug}`;
  }, [menu?.publicRsvpSlug]);

  const cookingDateOptions = useMemo(() => {
    if (!menuDate || !DATE_REGEX.test(menuDate)) return [];

    const { start } = getWeekRange(fromISODate(menuDate));
    return getWeekDates(start).map((date) => {
      const value = toISODate(date);
      return {
        value,
        label: formatDate(date, 'EEEE, MMM d'),
      };
    });
  }, [menuDate]);

  // Clear MENU-only fields when type changes to ANNOUNCEMENT, or default cooking date when date changes
  useEffect(() => {
    if (menuType === 'ANNOUNCEMENT') {
      setSelectedRecipes([]);
      setSelectedAssignedCatererId(null);
      setSelectedCookingDate(null);
      setValue('rsvpAudience', 'FMB_ONLY');
      return;
    }

    if (!menuDate || !DATE_REGEX.test(menuDate)) return;

    setSelectedCookingDate((current) => {
      if (current && cookingDateOptions.some((option) => option.value === current)) {
        return current;
      }
      return menuDate;
    });
    // cookingDateOptions is derived from menuDate, so no need to include it in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuType, menuDate]);

  // Load caterers (admin assignment only)
  const loadCaterers = useCallback(async () => {
    try {
      const response = await fetchMembers({ role: 'CATERER', page: 1, pageSize: 200 });
      setCaterers(response.members);
    } catch (error) {
      showToast('Failed to load caterers', 'error');
    }
  }, [showToast]);

  // Reset form when modal opens or menu changes
  useEffect(() => {
    if (open) {
      if (menu) {
        reset({
          date: menu.date,
          title: menu.title,
          type: menu.type,
          rsvpAudience: menu.rsvpAudience || 'FMB_ONLY',
          rsvpCutoffAt: toPacificDateInputValue(menu.rsvpCutoffAt),
        });
        // Populate selected recipes from menu.recipes (junction data)
        setSelectedRecipes(
          (menu.recipes || []).map((r) => ({
            menuRecipeId: r.menuRecipeId,
            recipeId: r.recipeId,
            name: r.name,
            category: r.category,
            isOptional: r.isOptional,
          }))
        );
        setSelectedAssignedCatererId(menu.assignedCatererId || null);
        setSelectedCookingDate(menu.cookingDate || menu.date);
        setUserEditedTitle(true); // Edit mode: treat existing title as user-set
      } else {
        const initialDate = defaultDate ? toISODate(defaultDate) : '';
        reset({
          date: initialDate,
          title: '',
          type: 'MENU',
          rsvpAudience: 'FMB_ONLY',
          rsvpCutoffAt: '',
        });
        setSelectedRecipes([]);
        setSelectedAssignedCatererId(null);
        setUserEditedTitle(false); // Create mode: enable auto-populate
        setSelectedCookingDate(initialDate || null);
      }
    }
  }, [open, menu, defaultDate, reset]);

  useEffect(() => {
    if (open) {
      loadCaterers();
    }
  }, [open, loadCaterers]);

  // Handle recipe selection changes — auto-populate title from recipe names
  const handleRecipesChange = useCallback((recipes: SelectedRecipe[]) => {
    setSelectedRecipes(recipes);
    // Auto-populate title until user manually edits it
    if (menuType === 'MENU' && recipes.length > 0 && !userEditedTitle) {
      const autoTitle = recipes.map((r) => r.name).join(', ');
      setValue('title', autoTitle, { shouldDirty: true });
    }
  }, [menuType, setValue, userEditedTitle]);

  const onSubmit = async (data: MenuFormData) => {
    // Validate based on type
    if (data.type === 'MENU') {
      if (selectedRecipes.length === 0) {
        showToast('Please select at least one recipe', 'error');
        return;
      }
      if (!selectedCookingDate) {
        showToast('Please select a cooking day', 'error');
        return;
      }
      if (!data.title?.trim()) {
        showToast('Please enter a menu title', 'error');
        return;
      }
    }
    if (data.type === 'ANNOUNCEMENT' && !data.title?.trim()) {
      showToast('Please enter an announcement', 'error');
      return;
    }

    try {
      const titleToSend = data.title?.trim() || '';
      const recipesToSend = data.type === 'MENU'
        ? selectedRecipes.map((r) => ({
            ...(r.menuRecipeId !== undefined && { menuRecipeId: r.menuRecipeId }),
            recipeId: r.recipeId,
            isOptional: r.isOptional,
          }))
        : [];
      const assignedCatererIdToSend = data.type === 'MENU' ? selectedAssignedCatererId : null;
      const rsvpCutoffAtToSend = data.type === 'MENU'
        ? cutoffDateToIso(data.rsvpCutoffAt)
        : null;
      const rsvpCutoffUpdate = data.type === 'MENU' && (rsvpCutoffAtToSend || !!menu?.rsvpCutoffAt)
        ? { rsvpCutoffAt: rsvpCutoffAtToSend }
        : {};

      if (isEditMode && menu) {
        await updateMenu(menu.id, {
          title: titleToSend,
          type: data.type,
          recipes: recipesToSend,
          cookingDate: data.type === 'MENU' ? selectedCookingDate : null,
          assignedCatererId: assignedCatererIdToSend,
          ...(data.type === 'MENU' ? { rsvpAudience: data.rsvpAudience, ...rsvpCutoffUpdate } : {}),
        });
        showToast('Menu updated successfully', 'success');
      } else {
        await createMenu({
          date: data.date,
          title: titleToSend,
          type: data.type,
          recipes: recipesToSend,
          cookingDate: data.type === 'MENU' ? selectedCookingDate : null,
          assignedCatererId: assignedCatererIdToSend,
          ...(data.type === 'MENU' ? { rsvpAudience: data.rsvpAudience, ...rsvpCutoffUpdate } : {}),
        });
        showToast('Menu created successfully', 'success');
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Operation failed';
      if (message.includes('already exists')) {
        showToast('A menu already exists for this date', 'error');
      } else {
        showToast(message, 'error');
      }
    }
  };

  const modalTitle = isEditMode ? 'Edit Menu' : 'Create Menu';
  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className="min-w-0 space-y-4">
      <div className={`grid gap-4 ${menuType === 'MENU' ? 'sm:grid-cols-2' : ''}`}>
        <div className="min-w-0 overflow-hidden space-y-2">
          <Label htmlFor="date">Date</Label>
          <Input
            id="date"
            type="date"
            {...register('date')}
            disabled={isEditMode}
            className={errors.date ? 'w-full min-w-0 max-w-full border-destructive' : 'w-full min-w-0 max-w-full'}
            style={{ minWidth: 0, maxWidth: '100%' }}
          />
          {errors.date && (
            <p className="text-sm text-destructive">{errors.date.message}</p>
          )}
        </div>

        {menuType === 'MENU' && (
          <div className="space-y-2">
            <Label htmlFor="cookingDate">Cooking Day <span className="text-destructive">*</span></Label>
          <Select
            value={selectedCookingDate ?? undefined}
            onValueChange={(value) => setSelectedCookingDate(value)}
          >
            <SelectTrigger id="cookingDate" className="w-full">
                <SelectValue placeholder="Select cooking day" />
              </SelectTrigger>
              <SelectContent>
                {cookingDateOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      </div>

      <div className={`grid gap-4 ${menuType === 'MENU' ? 'sm:grid-cols-2' : ''}`}>
        <div className="space-y-2">
          <Label htmlFor="type">Type</Label>
          <Select
            value={menuType}
            onValueChange={(value: 'MENU' | 'ANNOUNCEMENT') =>
              setValue('type', value)
            }
          >
            <SelectTrigger id="type" className="w-full">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MENU">Menu</SelectItem>
              <SelectItem value="ANNOUNCEMENT">Announcement</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {menuType === 'MENU'
              ? 'Regular meal with RSVP.'
              : 'Announcement only, with no RSVPs.'}
          </p>
        </div>

        {menuType === 'MENU' && (
          <div className="space-y-2">
            <Label htmlFor="rsvpAudience">100% Thaali</Label>
            <label
              htmlFor="rsvpAudience"
              className="flex min-h-10 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm"
            >
              <input
                id="rsvpAudience"
                type="checkbox"
                checked={isJamaatWide}
                onChange={(event) => setValue('rsvpAudience', event.target.checked ? 'JAMAAT_WIDE' : 'FMB_ONLY')}
                className="mt-0.5 h-4 w-4 rounded border-input text-primary"
              />
              <span className="font-medium leading-none">100% Thaali</span>
            </label>
          </div>
        )}

        {menuType === 'MENU' && (
          <div className="space-y-2">
            <Label htmlFor="rsvpCutoffAt">RSVP cutoff override</Label>
            <Input
              id="rsvpCutoffAt"
              type="date"
              {...register('rsvpCutoffAt')}
            />
            <p className="text-xs text-muted-foreground">
              Optional. Selected date closes at 11:59 PM Pacific. Leave blank to use the regular system weekly cutoff.
            </p>
          </div>
        )}
      </div>

      {menuType === 'MENU' && (
        <div className="space-y-2">
          <Label htmlFor="recipe">Recipes <span className="text-destructive">*</span></Label>
          <MultiRecipeSelector
            value={selectedRecipes}
            onChange={handleRecipesChange}
            disabled={isSubmitting}
          />
        </div>
      )}

      {menuType === 'MENU' ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Menu Title <span className="text-destructive">*</span></Label>
            <Input
              id="title"
              placeholder="e.g., Butter Chicken with Naan"
              {...register('title', {
                onChange: () => setUserEditedTitle(true),
              })}
              className={errors.title ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Auto-populated from recipe names. Can be customized.
            </p>
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignedCaterer">Assigned Caterer</Label>
            <Select
              value={selectedAssignedCatererId ?? 'NONE'}
              onValueChange={(value) => setSelectedAssignedCatererId(value === 'NONE' ? null : value)}
            >
              <SelectTrigger id="assignedCaterer" className="w-full">
                <SelectValue placeholder="Select caterer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Unassigned</SelectItem>
                {caterers.map((caterer) => (
                  <SelectItem key={caterer.id} value={caterer.id}>
                    {caterer.name} ({caterer.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {menu?.assignedCatererNameSnapshot && menu.assignedCatererId && menu.assignedCatererId === selectedAssignedCatererId && (
              <p className="text-xs text-muted-foreground">
                Snapshot: {menu.assignedCatererNameSnapshot}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="title">Announcement</Label>
          <Input
            id="title"
            placeholder="e.g., Kitchen Closed - Holiday"
            {...register('title')}
            className={errors.title ? 'border-destructive' : ''}
          />
          {errors.title && (
            <p className="text-sm text-destructive">{errors.title.message}</p>
          )}
        </div>
      )}

      {menuType === 'MENU' && publicRsvpLink && isJamaatWide && (
        <div className="space-y-2 rounded-md border p-3">
          <Label htmlFor="publicRsvpLink">Public RSVP Link</Label>
          <div className="flex gap-2">
            <Input id="publicRsvpLink" value={publicRsvpLink} readOnly className="min-w-0" />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => navigator.clipboard?.writeText(publicRsvpLink)}
              aria-label="Copy public RSVP link"
              title="Copy public RSVP link"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => window.open(publicRsvpLink, '_blank', 'noopener,noreferrer')}
              aria-label="Open public RSVP link"
              title="Open public RSVP link"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {isDesktop ? (
        <DialogFooter className="min-w-0 flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {/* Delete button - left side, only in edit mode */}
          {isEditMode && menu && onDelete && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                onOpenChange(false);
                onDelete(menu);
              }}
              disabled={isSubmitting}
              className="sm:mr-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}

          {/* Cancel and Save buttons - right side */}
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : isEditMode
                ? 'Update'
                : 'Create'}
            </Button>
          </div>
        </DialogFooter>
      ) : (
        <DialogFooter className="min-w-0 flex-col gap-2 sm:flex-col">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting
              ? 'Saving...'
              : isEditMode
              ? 'Update'
              : 'Create'}
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>

            {isEditMode && menu && onDelete && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  onDelete(menu);
                }}
                disabled={isSubmitting}
                className="flex-1 border-destructive/40 text-destructive hover:border-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </DialogFooter>
      )}
    </form>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
            <DialogDescription>
              Create or edit a menu
            </DialogDescription>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        onSwipeDown={() => onOpenChange(false)}
        description="Create or edit a menu"
      >
        <DrawerHeader className="border-b">
          <DrawerTitle>{modalTitle}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {formContent}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Convenience exports for clearer usage
 */
export function CreateMenuModal(
  props: Omit<MenuModalProps, 'menu'>
) {
  return <MenuModal {...props} menu={null} />;
}

export function EditMenuModal(
  props: Omit<MenuModalProps, 'defaultDate'> & { menu: Menu }
) {
  return <MenuModal {...props} defaultDate={undefined} />;
}
